import React from 'react';
import { Link } from 'react-router-dom';
import { LineChart, ColumnChart, BarList } from '../../admin/analytics/charts';
import { ClubPanel, PanelHead, PillButton, TagPill, INK, MUTED, SUBTLE, CHIP, CYAN, MAGENTA, RULE, label as lbl } from '../../club/clubUi';
import { money, monthDay } from '../vendor/vendorUi';

/**
 * Rich Analytics for Pro pages (Oct 2026, guide docs/guides/pro-rich-analytics.md): what the
 * admin panel knows, scoped to one page, drawn with the panel's own chart kit inside Portal
 * panels. The gate is the server's (`dash.richUnlocked`, read from the "Rich Analytics for Pro
 * pages" flag or an active Pro subscription) — this component only draws what it is given, and
 * draws the plan panel when `dash.rich` is missing.
 */

const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const DAY_LONG = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
const hourLabel = (h) => `${String(h).padStart(2, '0')}:00`;
const DEVICE = { phone: 'ON A PHONE', desktop: 'ON A COMPUTER', unknown: 'UNKNOWN' };
const CHANNEL = { email: 'EMAIL', sms: 'TEXT', notification: 'NOTIFICATION' };

export const fmtStat = (s) => {
  if (!s) return '—';
  switch (s.format) {
    case 'rate': return `${Math.round(s.value * 100)}%`;
    case 'cents': return money(s.value, { whole: true });
    case 'days': return `${s.value.toFixed(1)}d`;
    case 'ratio': return s.value.toFixed(1);
    default: return Math.round(s.value).toLocaleString('en-US');
  }
};
const deltaOf = (s) => (s?.delta == null ? null : `${s.delta >= 0 ? '+' : '−'}${Math.abs(Math.round(s.delta * 100))}%`);

/** The plan panel: what a locked page sees where the rich block would be. */
export function RichAnalyticsLocked({ phone }) {
  return (
    <ClubPanel phone={phone} bg="#000" fg="#fff" data-testid="rich-locked" style={{ gap: 18 }}>
      <span className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <span style={lbl(12, '.16em')}>RICH ANALYTICS</span>
        <TagPill bg="#000" fg="#fff" ring={CYAN} size={9}>PRO PLAN</TagPill>
      </span>
      <span style={{ fontSize: phone ? 18 : 24, fontWeight: 800, lineHeight: 1.2, letterSpacing: '.02em', maxWidth: 640 }}>
        Who comes back and when they look. Your regulars. Every offer and every event, send by send.
      </span>
      <span style={{ fontSize: 12, lineHeight: 1.65, color: '#C4C4C4', maxWidth: 560 }}>
        The deeper numbers are part of the Pro plan: new vs returning viewers, the hour-by-hour heat map, where they are, your audience growth, the people who keep coming back, offer by offer, ticket sales on every event on your page, and what each send did.
      </span>
      <span className="flex flex-wrap" style={{ gap: 10 }}>
        <PillButton as={Link} to="/pro/desk" variant="ring" size={46} data-testid="rich-plan-cta">GET THE PRO PLAN</PillButton>
      </span>
    </ClubPanel>
  );
}

function Tile({ s, phone, labelText }) {
  const d = deltaOf(s);
  const up = s?.delta > 0;
  return (
    <div className="flex min-w-0 flex-col" style={{ gap: 8, padding: phone ? '12px 12px' : '14px 16px', borderRadius: 18, background: '#F7F7F7' }}>
      <span className="truncate" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: MUTED }}>{labelText || s?.label || '—'}</span>
      <span className="flex items-baseline" style={{ gap: 8 }}>
        <span style={{ fontSize: phone ? 20 : 26, fontWeight: 800, lineHeight: 1 }}>{fmtStat(s)}</span>
        {d && <span style={{ padding: '2px 7px', borderRadius: 999, background: up ? '#DCFCE7' : CHIP, color: up ? '#166534' : MUTED, fontSize: 10, fontWeight: 800 }}>{d}</span>}
      </span>
    </div>
  );
}

function Tiles({ stats, phone, cols }) {
  return (
    <div className="grid" style={{ gridTemplateColumns: `repeat(${phone ? 2 : cols || Math.min(stats.length, 6)}, minmax(0,1fr))`, gap: 8 }}>
      {stats.map((s) => <Tile key={s.key} s={s} phone={phone} />)}
    </div>
  );
}

const note = (text) => <span style={{ paddingTop: 10, borderTop: RULE, fontSize: 11, lineHeight: 1.6, color: MUTED }}>{text}</span>;
const empty = (text) => <span style={{ fontSize: 12, color: MUTED }}>{text}</span>;

/** 7 × 24 heat grid: the hour and the weekday the page is looked at. */
function HeatGrid({ heat, peakHour, peakWeekday }) {
  const max = Math.max(1, ...heat.flat());
  return (
    <div className="flex flex-col" style={{ gap: 10 }} data-testid="heat-grid">
      <div className="grid" style={{ gridTemplateColumns: '34px minmax(0,1fr)', gap: 6 }}>
        <span />
        <div className="grid" style={{ gridTemplateColumns: 'repeat(24, minmax(0,1fr))', gap: 2 }}>
          {Array.from({ length: 24 }, (_, h) => <span key={h} className="text-center" style={{ fontSize: 8, fontWeight: 700, color: SUBTLE }}>{h % 3 === 0 ? h : ''}</span>)}
        </div>
        {heat.map((row, d) => (
          <React.Fragment key={d}>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.1em', color: d === peakWeekday ? INK : MUTED, lineHeight: '14px' }}>{DAYS[d]}</span>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(24, minmax(0,1fr))', gap: 2 }}>
              {row.map((n, h) => (
                <span key={h} title={`${DAY_LONG[d]} ${hourLabel(h)} · ${n} view${n === 1 ? '' : 's'}`} style={{ height: 14, borderRadius: 3, background: n === 0 ? CHIP : `rgba(255,0,170,${0.18 + 0.82 * (n / max)})`, boxShadow: d === peakWeekday && h === peakHour && n > 0 ? `inset 0 0 0 2px ${INK}` : 'none' }} />
              ))}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function Table({ cols, rows, phone, testId }) {
  const show = phone ? cols.filter((c) => !c.wide) : cols;
  return (
    <div className="flex flex-col" data-testid={testId}>
      <div className="grid" style={{ gridTemplateColumns: show.map((c) => c.w || 'minmax(0,1fr)').join(' '), gap: 10, padding: '0 0 8px' }}>
        {show.map((c) => <span key={c.key} className={c.right ? 'text-right' : ''} style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: SUBTLE }}>{c.label}</span>)}
      </div>
      {rows.map((row, i) => (
        <div key={row.key || i} className="grid items-center" style={{ gridTemplateColumns: show.map((c) => c.w || 'minmax(0,1fr)').join(' '), gap: 10, padding: '10px 0', borderTop: RULE }}>
          {show.map((c) => <span key={c.key} className={`min-w-0 ${c.right ? 'text-right' : 'truncate'}`} style={{ fontSize: c.key === 'title' ? 12 : 12, fontWeight: c.right ? 800 : 600, color: c.muted ? MUTED : INK }}>{c.render ? c.render(row) : row[c.key]}</span>)}
        </div>
      ))}
    </div>
  );
}

export default function RichAnalytics({ dash, phone, mid, gap = 16 }) {
  if (!dash) return null;
  const rich = dash.rich;
  if (!dash.richUnlocked || !rich) return <RichAnalyticsLocked phone={phone} />;

  const views = phone ? (dash.views || []).slice(-14) : (dash.views || []);
  const prev = rich.prevViews || [];
  const offset = (dash.views || []).length - views.length;
  const trend = views.map((p, i) => ({ date: p.day, views: p.count, prev: prev[i + offset]?.count || 0 }));
  const byHour = (rich.byHour || []).map((n, h) => ({ key: `h${h}`, label: hourLabel(h), value: n }));
  const byDay = (rich.byWeekday || []).map((n, d) => ({ key: `d${d}`, label: DAYS[d], value: n }));
  const totalViews = (rich.byHour || []).reduce((a, b) => a + b, 0);
  const list = (obj, labelFor) => Object.entries(obj || {}).sort((a, b) => b[1] - a[1]).map(([k, n]) => ({ label: labelFor ? labelFor(k) : k.toUpperCase(), count: n }));
  const audience = (rich.audienceSeries || []).map((p) => ({ date: p.day, followers: p.followers, contacts: p.contacts }));
  const tickets = (rich.ticketSeries || []).map((p) => ({ date: p.day, tickets: p.count, buyers: p.uniques }));
  const peak = rich.peakHour != null && rich.peakWeekday != null ? `${DAY_LONG[rich.peakWeekday]} · ${hourLabel(rich.peakHour)}` : null;
  const two = mid ? 'minmax(0,1fr)' : 'minmax(0,1fr) minmax(0,1fr)';
  const three = mid ? 'minmax(0,1fr)' : 'repeat(3, minmax(0,1fr))';

  return (
    <div className="flex flex-col" style={{ gap }} data-testid="rich-analytics">
      <div className="flex flex-wrap items-center justify-between" style={{ gap: 10, padding: '6px 4px 0' }}>
        <span className="flex flex-wrap items-center" style={{ gap: 10 }}>
          <span style={{ fontSize: phone ? 14 : 16, fontWeight: 800, letterSpacing: '.08em' }}>RICH ANALYTICS</span>
          <TagPill bg="#fff" fg={INK} ring={CYAN} size={9}>PRO PLAN</TagPill>
        </span>
        {dash.richReason === 'everyone' && <span style={{ fontSize: 10, letterSpacing: '.1em', color: MUTED }}>INCLUDED WITH EVERY PAGE FOR NOW</span>}
      </div>

      <ClubPanel phone={phone}>
        <LineChart
          title="THIS PERIOD VS THE LAST"
          label="Page views this period against the previous one"
          points={trend}
          series={[{ key: 'views', label: 'THIS PERIOD' }, { key: 'prev', label: 'LAST PERIOD' }]}
          height={phone ? 160 : 220}
          tipTitleFor={(i) => `${monthDay(trend[i].date)} · vs ${prev[i + offset] ? monthDay(prev[i + offset].day) : '—'}`}
        />
        {note('The dashed line is the same day in the period before this one — the honest comparison for a page whose weeks look alike.')}
      </ClubPanel>

      <div className="grid items-stretch" style={{ gridTemplateColumns: two, gap }}>
        <ClubPanel phone={phone}>
          <PanelHead title="WHO IS LOOKING" />
          <Tiles stats={rich.viewers || []} phone={phone} cols={3} />
          {note('NEW never looked before this period; RETURNING had. CAME BACK THIS PERIOD looked on two different days inside it. SIGNED IN is the share of views by a Portal account — the rest are browsers.')}
        </ClubPanel>
        <ClubPanel phone={phone}>
          <PanelHead title="WHEN THEY LOOK" right={peak ? `PEAK · ${peak}` : null} />
          {totalViews === 0 ? empty('No views yet.') : <HeatGrid heat={rich.heat || []} peakHour={rich.peakHour} peakWeekday={rich.peakWeekday} />}
          {note('Every view on the viewer’s clock, by weekday and hour. Send and post just before the bright cells, not after.')}
        </ClubPanel>
      </div>

      <div className="grid items-stretch" style={{ gridTemplateColumns: two, gap }}>
        <ClubPanel phone={phone}>
          <PanelHead title="BY HOUR" />
          <ColumnChart items={byHour} height={150} />
        </ClubPanel>
        <ClubPanel phone={phone}>
          <PanelHead title="BY WEEKDAY" />
          <ColumnChart items={byDay} height={150} />
        </ClubPanel>
      </div>

      <div className="grid items-stretch" style={{ gridTemplateColumns: three, gap }}>
        <ClubPanel phone={phone}>
          <PanelHead title="WHERE THEY ARE" />
          <BarList items={list(dash.regions, (k) => k.toUpperCase())} />
        </ClubPanel>
        <ClubPanel phone={phone}>
          <PanelHead title="PHONE OR COMPUTER" />
          <BarList items={list(dash.devices, (k) => DEVICE[k] || k.toUpperCase())} />
        </ClubPanel>
        <ClubPanel phone={phone}>
          <PanelHead title="WHAT THEY SEARCHED" />
          <BarList items={list(dash.searchTerms, (k) => k.toUpperCase())} />
        </ClubPanel>
      </div>

      <ClubPanel phone={phone}>
        <PanelHead title="YOUR AUDIENCE" />
        <Tiles stats={rich.audience || []} phone={phone} cols={6} />
        {audience.length > 0 && (
          <LineChart
            label="New followers and list sign-ups by day"
            points={phone ? audience.slice(-14) : audience}
            series={[{ key: 'followers', label: 'NEW FOLLOWERS' }, { key: 'contacts', label: 'JOINED THE LIST' }]}
            height={phone ? 140 : 170}
          />
        )}
        {note('Followers hear about the page on Portal; the list is who you can email or text. VIEWERS WHO FOLLOWED is new followers over unique viewers this period.')}
      </ClubPanel>

      <div className="grid items-stretch" style={{ gridTemplateColumns: mid ? 'minmax(0,1fr)' : '420px minmax(0,1fr)', gap }}>
        <ClubPanel phone={phone} data-testid="regulars">
          <PanelHead title="YOUR REGULARS" right={(rich.regulars || []).length ? `TOP ${(rich.regulars || []).length}` : null} />
          {(rich.regulars || []).length === 0 && empty('Nobody yet. People show up here once they follow you, join your list, claim an offer or buy a ticket.')}
          <div className="flex flex-col">
            {(rich.regulars || []).map((p, i) => (
              <div key={p.userId} className="flex items-center" style={{ gap: 12, padding: '10px 0', borderTop: i ? RULE : 'none' }}>
                {p.avatarUrl
                  ? <img src={p.avatarUrl} alt="" className="flex-shrink-0 rounded-full object-cover" style={{ width: 36, height: 36 }} />
                  : <span className="flex flex-shrink-0 items-center justify-center rounded-full" style={{ width: 36, height: 36, background: CHIP, fontSize: 11, fontWeight: 800 }}>{(p.displayName || '?').slice(0, 1).toUpperCase()}</span>}
                <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
                  <span className="flex items-center" style={{ gap: 8 }}>
                    {p.handle ? <Link to={`/${p.handle}`} className="truncate" style={{ fontSize: 13, fontWeight: 800, color: INK }}>{p.displayName}</Link> : <span className="truncate" style={{ fontSize: 13, fontWeight: 800 }}>{p.displayName}</span>}
                    {p.follows && <TagPill size={8}>FOLLOWS</TagPill>}
                    {p.onList && <TagPill size={8} bg={INK} fg="#fff">ON YOUR LIST</TagPill>}
                  </span>
                  <span style={{ fontSize: 10, letterSpacing: '.06em', color: MUTED }}>
                    {[p.views ? `${p.views} VIEW${p.views === 1 ? '' : 'S'}` : null, p.claims ? `${p.claims} CLAIM${p.claims === 1 ? '' : 'S'}` : null, p.redemptions ? `${p.redemptions} WALKED IN` : null, p.tickets ? `${p.tickets} TICKET${p.tickets === 1 ? '' : 'S'}` : null].filter(Boolean).join(' · ')}
                    {p.lastSeen ? ` · LAST ${monthDay(p.lastSeen)}` : ''}
                  </span>
                </span>
              </div>
            ))}
          </div>
          {note('Only people with a relationship to the page — they follow you, joined your list, claimed an offer or bought a ticket. Everyone else stays an anonymous count.')}
        </ClubPanel>
        <ClubPanel phone={phone}>
          <PanelHead title="OFFER BY OFFER" />
          {(rich.offerRows || []).length === 0 ? empty('No offers yet.') : (
            <Table phone={phone} testId="offer-rows" rows={rich.offerRows.map((o) => ({ ...o, key: o.offerId }))} cols={[
              { key: 'title', label: 'OFFER', render: (o) => <span className="flex min-w-0 items-center" style={{ gap: 8 }}><span className="truncate">{o.title}</span>{o.phase !== 'live' && <TagPill size={8}>{o.phase.replace(/_/g, ' ').toUpperCase()}</TagPill>}</span> },
              { key: 'views', label: 'SAW IT', w: '64px', right: true },
              { key: 'claims', label: 'CLAIMED', w: '70px', right: true },
              { key: 'redemptions', label: 'WALKED IN', w: '84px', right: true },
              { key: 'valueCents', label: 'COST', w: '76px', right: true, wide: true, render: (o) => money(o.valueCents, { whole: true }) },
            ]} />
          )}
        </ClubPanel>
      </div>

      <ClubPanel phone={phone} data-testid="events-rich">
        <PanelHead title="EVENTS ON YOUR PAGE" />
        <Tiles stats={rich.events || []} phone={phone} cols={5} />
        {(rich.eventRows || []).length === 0 ? empty('Nothing linked yet — put an event on the page from the PAGE tab.') : (
          <Table phone={phone} testId="event-rows" rows={rich.eventRows.map((e) => ({ ...e, key: e.eventId }))} cols={[
            { key: 'title', label: 'EVENT', render: (e) => <Link to={e.path} className="truncate" style={{ color: INK, fontWeight: 800 }}>{e.title}</Link> },
            { key: 'when', label: 'WHEN', w: '74px', muted: true, render: (e) => monthDay(e.dateTime) },
            { key: 'status', label: 'STATUS', w: '96px', wide: true, muted: true, render: (e) => (e.status || '').replace(/_/g, ' ').toUpperCase() },
            { key: 'tickets', label: 'TICKETS', w: '66px', right: true },
            { key: 'buyers', label: 'BUYERS', w: '62px', right: true, wide: true },
            { key: 'revenueCents', label: 'SALES', w: '84px', right: true, render: (e) => money(e.revenueCents, { whole: true }) },
          ]} />
        )}
        {tickets.length > 0 && tickets.some((t) => t.tickets > 0) && (
          <LineChart label="Tickets sold by day" points={phone ? tickets.slice(-14) : tickets} series={[{ key: 'tickets', label: 'TICKETS' }, { key: 'buyers', label: 'BUYERS' }]} height={phone ? 140 : 170} />
        )}
        {note('Tickets and sales on every event linked to this page, all time per row; the tiles are this period. Sales are what guests paid for tickets, before fees.')}
      </ClubPanel>

      <ClubPanel phone={phone} data-testid="sends-rich">
        <PanelHead title="WHAT YOUR SENDS DID" />
        <Tiles stats={rich.sendStats || []} phone={phone} cols={4} />
        {(rich.sendRows || []).length === 0 ? empty('No sends in this period. Email or text your people from the SEND tab.') : (
          <Table phone={phone} testId="send-rows" rows={rich.sendRows.map((s) => ({ ...s, key: s.id }))} cols={[
            { key: 'subject', label: 'SEND', render: (s) => <span className="flex min-w-0 items-center" style={{ gap: 8 }}><span className="truncate">{s.subject}</span><TagPill size={8}>{CHANNEL[s.channel] || s.channel.toUpperCase()}</TagPill></span> },
            { key: 'sentAt', label: 'SENT', w: '74px', muted: true, render: (s) => monthDay(s.sentAt) },
            { key: 'recipients', label: 'REACHED', w: '72px', right: true },
            { key: 'bounced', label: 'BOUNCED', w: '72px', right: true, wide: true },
            { key: 'viewsAfter', label: 'VIEWS · 48H', w: '84px', right: true, render: (s) => <span style={{ color: s.viewsAfter > 0 ? MAGENTA : INK }}>{s.viewsAfter}</span> },
          ]} />
        )}
        {note('VIEWS · 48H is how many times the page was opened in the two days after the send went out — the lift you can see.')}
      </ClubPanel>
    </div>
  );
}
